import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

export const MANAGED_MAPPER_MODES = ["agentic", "code"] as const;
export type ManagedMapperMode = (typeof MANAGED_MAPPER_MODES)[number];

export const MANAGED_MAPPER_MODEL_TIERS = [
  "fast",
  "balanced",
  "smart",
  "reasoning",
] as const;
export type ManagedMapperModelTier =
  (typeof MANAGED_MAPPER_MODEL_TIERS)[number];

export const managedMapperStep = defineStepConfig({
  type: "managed_mapper",
  name: "Managed Mapper",
  description:
    "AI-powered data mapper that transforms input data to match an output schema. Uses an LLM in agentic mode to infer mappings from context and schema definitions.",
  category: "Transformation",

  inputSchema: z.record(z.string(), z.unknown()),
  outputSchema: z.record(z.string(), z.unknown()),

  configSchema: z.object({
    mode: z
      .enum(MANAGED_MAPPER_MODES)
      .default("agentic")
      .describe("Execution mode: agentic (LLM) or code (TypeScript/Python)."),
    modelTier: z
      .enum(MANAGED_MAPPER_MODEL_TIERS)
      .default("balanced")
      .describe("Which model tier to use for inference in agentic mode."),
    memoryBankNoteId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Memory bank note containing user context and accumulated learnings.",
      ),
    inputSchemaDefinitionId: z
      .string()
      .uuid()
      .optional()
      .describe("Schema definition whose fields define the input shape."),
    inputSchemaDefinitionVersionId: z
      .string()
      .uuid()
      .optional()
      .describe("Pinned immutable version of the input schema."),
    outputSchemaDefinitionId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Schema definition whose fields define the output shape and output sockets.",
      ),
    outputSchemaDefinitionVersionId: z
      .string()
      .uuid()
      .optional()
      .describe("Pinned immutable version of the output schema."),
    maxRetries: z
      .number()
      .int()
      .min(0)
      .max(3)
      .default(2)
      .describe(
        "Number of in-handler retry attempts on validation failure before failing the step.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },

  evalConfig: {
    evalOutputKeys: [],
    outputFormat: "object" as const,
    contextOutputKeys: ["__mapping_reasoning"],
  },
});
